/**
 * Environment detection helpers. Pure functions, no side effects.
 *
 * `useIosPoller` is the only flag the watcher acts on for scheduling;
 * the rest are surfaced for the debug panel.
 */

import { POST_RELOAD_FRESH_WINDOW_MS } from "./constants";

export interface EnvDetection {
  isIOS: boolean;
  isIPadOS: boolean;
  isWebKit: boolean;
  useIosPoller: boolean;
}

export function detectEnv(): EnvDetection {
  try {
    if (typeof navigator === "undefined") {
      return { isIOS: false, isIPadOS: false, isWebKit: false, useIosPoller: false };
    }
    const ua = navigator.userAgent || "";
    const platform = (navigator as any).platform || "";
    const maxTouch = (navigator as any).maxTouchPoints || 0;
    // iPadOS 13+ masquerades as desktop Safari (platform MacIntel, no "iPad" in UA).
    const isIPadOS = platform === "MacIntel" && maxTouch > 1;
    const isIOS = /iPad|iPhone|iPod/.test(ua) || isIPadOS;
    // WebKit (any browser engine on iPad is WebKit, including Chrome/Firefox on iOS).
    const isWebKit = /AppleWebKit/.test(ua) && !/Chrome\/.*Android/.test(ua);
    // Route any iOS device OR any WebKit-with-touch (iPad Chrome/Firefox) to the poller.
    const useIosPoller =
      isIOS || (isWebKit && maxTouch > 1 && /Mac|iPad|iPhone/i.test(platform + ua));
    return { isIOS, isIPadOS, isWebKit, useIosPoller };
  } catch {
    return { isIOS: false, isIPadOS: false, isWebKit: false, useIosPoller: false };
  }
}

export function isIOSEnv(): boolean {
  return detectEnv().useIosPoller;
}

export function isCapacitorNative(): boolean {
  try {
    const protocol = window.location.protocol;
    if (protocol === "capacitor:" || protocol === "ionic:" || protocol === "file:") {
      return true;
    }
    // @ts-ignore - Capacitor global from native bridge
    const cap = (window as any).Capacitor;
    return !!(cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform());
  } catch {
    return false;
  }
}

export function isPreviewOrIframe(): boolean {
  try {
    if (window.self !== window.top) return true;
  } catch {
    return true;
  }
  const h = window.location.hostname;
  return (
    h.startsWith("id-preview--") ||
    h.startsWith("preview--") ||
    h.endsWith(".lovableproject.com") ||
    h.endsWith(".lovableproject-dev.com") ||
    h.endsWith(".beta.lovable.dev")
  );
}

export function isStripeReturn(): boolean {
  try {
    const p = new URLSearchParams(window.location.search);
    return p.has("session_id") || p.has("payment_intent") || p.has("checkout_session_id");
  } catch {
    return false;
  }
}

export function isPostReloadWindow(): boolean {
  try {
    const p = new URLSearchParams(window.location.search);
    const fresh = p.get("fresh");
    if (!fresh) return false;
    const elapsed = Date.now() - Number(fresh);
    return Number.isFinite(elapsed) && elapsed < POST_RELOAD_FRESH_WINDOW_MS;
  } catch {
    return false;
  }
}
